import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import * as Sentry from '@sentry/react';
import { RootState } from 'store';

import { flightsSearchParameters } from 'action-creators/flights';
import { confirmFlightSolicitation, fetchFlightDetailsBySector, validateFlight } from 'actions/flights';
import useFetching from 'hooks/useFetching';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { ConfirmMessage } from 'models/confirmMessage';
import { FlightResultResponse } from 'models/flightSearch';
import { FlightValidateResponse } from 'models/flightValidate';
import { FlightValidationResponse, FlightValidationResponseBySector } from 'models/flightValidation';
import FlightDetails from 'screens/components/FlightDetails';
import { TravelArrangerUsersAllowed } from 'services/userService';
import {  extractErrorMessage } from 'utils/generic';

const INCOMPLETE_PROFILE_ERROR_MESSAGES = ["'Gender'", "'Phone'", "'DOB'"];

interface Props {
  show: boolean;
  flight?: FlightResultResponse;
  optionsIds?: string[];
  upSellId?: string;
  upSellOptionId?: number;
  onClose: () => void;
  travelers?: TravelArrangerUsersAllowed[];
  index?: number;
  zIndex?: number;
}

const FlightDetailsBySectorContainer = ({
  show,
  optionsIds,
  onClose,
  upSellId,
  upSellOptionId = 0,
  travelers,
  index,
  zIndex,
}: Props) => {
  const toast = useToast();
  const dispatch = useDispatch();
  const { t } = useStoreTranslation();
  const history = useHistory();

  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<FlightValidationResponseBySector | undefined>();
  const [showChurning, setShowChurning] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [confirmFlowMessage, setConfirmFlowMessage] = useState<ConfirmMessage>({});
  const [showConfirmFlow, setShowConfirmFlow] = useState(false);

  const { searchId, sessionToken, userId, fullState,distanceRestriction,
    lowestLogicalFare,airValidateBySector,baggageIncluded,departurePoint,
    arrivalPoint,departureDate,cabin,returnDate,departureTime,returnTime,directFlight,multiDestinationTrips,flightStretch} = useSelector((state: RootState) => ({
    searchId: state.flights.search?.searchId,
    sessionToken: state.user.accountResult?.sessionToken,
    userId: state.user.accountResult?.id,
    fullState: state,
    distanceRestriction: state.flights.search?.distanceRestriction,
    lowestLogicalFare: state.flights.search?.lowestLogicalFare,
    airValidateBySector : state.flights.optionsIds,
    baggageIncluded: state.flights.baggageIncluded,
    departurePoint: state.flights.departurePoint,
    arrivalPoint: state.flights.arrivalPoint,
    departureDate: state.flights.departureDate,
    cabin: state.flights.cabin,
    returnDate: state.flights.returnDate,
    departureTime: state.flights.departureTime,
    returnTime: state.flights.returnTime,
    directFlight: state.flights.directFlight,
    multiDestinationTrips: state.flights.multiDestinationTrips,
    flightStretch: state.flights.flightStretch
  }));

  const search = useSelector((state: RootState) => state.flights);
  const userAccount = useSelector((state: RootState) => state.user?.accountResult);

  useFetching(() => {
    if (show) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }

    if (optionsIds.length===0 || !sessionToken || !searchId || !userId) return;

    const getDetails = async () => {
      try {
        setIsLoading(true);
        const {
          data: { data, succeeded },
        } = await fetchFlightDetailsBySector(
          searchId,
          sessionToken,
          userId,
          distanceRestriction,
          lowestLogicalFare,
          travelers && travelers.length > 0 ? travelers.length : 1,
          airValidateBySector,
          baggageIncluded,
          departurePoint,
          arrivalPoint,
          departureDate,
          cabin,
          returnDate,
          departureTime,
          returnTime,
          directFlight,
          multiDestinationTrips,
          flightStretch,
        );
        // if (data.result === AirValidationResultType.PriceDifferent) {
        //   toast.add({ type: 'warning', message: t('flightDetails.error.priceChanged') });
        // } else if (!succeeded) {
        //   Sentry.addBreadcrumb({
        //     category: 'flight',
        //     message: 'Flight validation solicitation not succeeded',
        //   });
        //   throw new Error();
        // }

        setData({
          ...data
        });
;

        setIsLoading(false);
      } catch (ex) {
        console.error(ex);
        const status = ex?.response?.status;

        let message = t('flightDetails.error.genericValidate');
        if (status === 400) {
          message = t('flightDetails.error.unavailable');
          handleUpsellUnavailable(ex);
          return;
        }

        Sentry.addBreadcrumb({
          category: 'flight',
          message: 'Flight validation solicitation error',
          data: { error: extractErrorMessage(ex), message },
        });

        setData(undefined);
        setIsLoading(false);

        toast.add({
          type: 'danger',
          message,
        });
        onClose();

        Sentry.captureMessage('[FlightDetails] Could not retrieve itinerary validation');
      }
    };

    if (optionsIds.length > 0 && show) getDetails();
  }, [optionsIds, show]);

  const handleConfirm = async (
    flight: FlightValidationResponse,
    justifyForBreakingRules: string,
    reason: string,
    costCenterId?: string,
    breakRulesMessageId?: string,
    card?:string    
  ) => {
    setIsCreating(true);
    try {
      
      const validationIds = flight.airValidateResponse.map(x => x.validationId);
      const optionsIds = airValidateBySector.map(x => parseInt(x));
  
      const responseValidate = await validateFlight(
        flight,
        justifyForBreakingRules,
        reason,
        fullState,
        upSellId,
        upSellOptionId,
        costCenterId,
        card,
        validationIds,
        optionsIds
      );

      
      const flightValidate: FlightValidateResponse = {
        documentId: responseValidate.data.documentId,
        validateResultType: ''
      };
      await dispatch<any>(
        confirmFlightSolicitation(
          flight,
          justifyForBreakingRules,
          reason,
          flightValidate,
          validationIds, 
          upSellId,
          upSellOptionId,
          costCenterId,
          breakRulesMessageId,
          card
        ),
      );
      setShowSuccess(true);
    } catch (ex) {
      const status = ex?.response?.status;
      const error = extractErrorMessage(ex);
      setIsCreating(false);

      let message = t('flightDetails.error.generic');
      if (INCOMPLETE_PROFILE_ERROR_MESSAGES.some((msg) => error?.startsWith(msg))) {
        message = t('flightDetails.error.incompleteProfile');
      } else if (error?.includes('Churning')) {
        setShowChurning(true);
        message = '';
      } else if (error?.indexOf('travel arranger is allowed') !== -1) {
        toast.add({
          type: 'danger',
          message: t('flightDetails.error.userDoesNotBelongToTheTravelArranger'),
        });
        return;
      } else if (error?.indexOf('Debes pertenecer a un grupo') !== -1) {
        toast.add({
          type: 'danger',
          message: t('flightDetails.error.userMustBeAssignedToGroup'),
        });
        return;
      } else if (status === 400) {
        message = t('flightDetails.error.unavailable');
        handleFlightUnavailable(error);
        return;
      }
    }
  };

  const handleFlightUnavailable = (error?: string) => {
    console.log(error)
    if (error?.indexOf('Duplex') > -1) {
      setConfirmFlowMessage({
        text: t('flightDetails.error.duplex.text'),
        button: t('flightDetails.error.duplex.button'),
        title: t('flightDetails.error.duplex.title'),
      });
    } else {
      setConfirmFlowMessage({
        text: t('flightDetails.error.unavailable.text'),
        button: t('flightDetails.error.unavailable.button'),
        title: t('flightDetails.error.unavailable.title'),
      });
    }

    setShowConfirmFlow(true);
  };

  const handleUpsellUnavailable = (error?: any) => {
    console.log(error)
      setConfirmFlowMessage({
        text: t('flightDetails.error.unavailableUpsell.text'),
        button: t('flightDetails.error.unavailableUpsell.button'),
        title: t('flightDetails.error.unavailableUpsell.title'),
      });

    setShowConfirmFlow(true);
  };

  const reloadSearch = async () => {
    if (travelers === undefined || travelers?.length === 0) {
      const currentUserAsTraveler: TravelArrangerUsersAllowed = {
        id: userAccount.id,
        userId: userAccount.id,
        name: userAccount.name,
        lastName: userAccount.lastName,
        email: userAccount.email,
        phoneNumber: userAccount.phoneNumber,
        userGroupName: userAccount.userGroupName,
        userGroupId: userAccount.userGroupId,
        favorite: true, // TODO fijar ese campo
        guest: userAccount.isGuestBookAllowed, // TODO fijar ese campo
        birthDate: userAccount.birthDate.toString(),
        country: userAccount.country,
        documentNumber: userAccount.documentNumber,
        gender: userAccount.gender,
        passportNumber: userAccount.passportNumber,
      };

      travelers.push(currentUserAsTraveler);
    }

    await dispatch(
      flightsSearchParameters({
        baggageIncluded: fullState.flights.baggageIncluded,
        departurePoint: fullState.flights.departurePoint,
        departureDate: fullState.flights.departureDate,
        arrivalPoint: fullState.flights.arrivalPoint,
        cabin: fullState.flights.cabin,
        returnDate: fullState.flights.returnDate,
        travelers,
        departureTime: fullState.flights.departureTime,
        returnTime: fullState.flights.returnTime,
        directFlight: fullState.flights.directFlight,
        multiDestinationTrips: search?.multiDestinationTrips,
        flightStretch: search?.flightStretch,
      }),
    );

    setIsLoading(false);
    setShowConfirmFlow(false);
    setIsCreating(false);
    history.replace('/corporate/flights');
  };

  const handleConfirmFlow = async () => {
    reloadSearch();
  };

  const handleConfirmFlowClose = () => {
    reloadSearch();
  };

  const handleClickBack = () => {  
    history.goBack();
  };

  return (
    <FlightDetails
      show={show}
      creating={isCreating}
      loading={isLoading}
      details={data}
      onClose={handleClickBack}
      onConfirm={handleConfirm}
      showChurning={showChurning}
      setShowChurning={setShowChurning}
      travelers={travelers}
      onConfirmFlow={handleConfirmFlow}
      onConfirmFlowClose={handleConfirmFlowClose}
      confirmFlowMessage={confirmFlowMessage}
      showConfirmFlow={showConfirmFlow}
      zIndex={zIndex}
      isBySector={true}
    />
  );
};

export default FlightDetailsBySectorContainer;
